import {
  colors,
  ImpButton,
  ImpCardThumbnail,
  ImpConfirmAddressPage,
  ImpContainer,
  ImpDivider,
  ImpFlex,
  ImpSpacer,
  ImpText,
} from '@imprint-payments/imprint-ui';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { EVENT } from '../../analytics/analyticsConsts';
import { confirm, requestPhysicalCard } from '../../api/api';
import { setRedirectToLogin } from '../../app/acceptScreenSlice';
import {
  CARD_SELECTION_SCREEN,
  setCardDesignSelectionScreen,
  setSelectedCardDesignID,
} from '../../app/cardDesignSlice';
import { useAppSelector, useImpState } from '../../app/hooks';
import {
  CardDesignOption,
  CardDesignStatus,
} from '../../app/merchantDataSlice';
import ButtonServicing, {
  ButtonColorScheme,
} from '../../components/buttonServicing/ButtonServicing';
import Spinner from '../../components/spinner/Spinner';
import { ANALYTICS } from '../../firebase/firebase';
import {
  convertAmountToString,
  redirectToAccountWithCookie,
} from '../../utils/functionUtils';
import styles from './CardDesign.module.css';

function CardDesignConfirmation() {
  const {
    language,
    merchantData,
    consumerInfo,
    appStatus,
    apiScreenState,
    dispatch,
  } = useImpState();
  const cardDesignOptionsImages = merchantData.cardDesignOptionsImages;
  const selectionID = useAppSelector(
    (state) => state.cardDesignSelector.selectedCardDesignID
  );
  const productUUID = useAppSelector(
    (state) => state.merchantDataSelector.productUUID
  );
  const multiCardUser = useAppSelector(
    (state) => state.loginSelector.multiCardUser
  );

  useEffect(() => {
    ANALYTICS.logEvent(EVENT.CONFIRM_ADDRESS_VIEWED);
  }, []);

  const handleConfirmAddressClicked = () => {
    const isMultiCard = multiCardUser === true ? 'true' : 'false';
    ANALYTICS.logEvent(EVENT.CONFIRM_ADDRESS_CONFIRM_CLICKED);
    dispatch(
      requestPhysicalCard(consumerInfo.auth.token, appStatus.productAccountUUID)
    ).then(() => {
      redirectToAccountWithCookie(
        consumerInfo.auth.token,
        apiScreenState.deviceID,
        consumerInfo.consumerUUID,
        productUUID,
        isMultiCard
      );
    });
  };

  function getAddress() {
    const address = consumerInfo.consumerProfile.address;
    return {
      line1: address.streetLine1,
      line2: address.streetLine2,
      line3: `${address.city}, ${address.state} ${address.zipCode}`,
    };
  }

  if (appStatus.productAccountUUID === '') {
    return <Spinner />;
  }

  return (
    <ImpConfirmAddressPage
      pageState={'enabled'}
      displayImage={cardDesignOptionsImages[selectionID].render}
      title={language.confirmHomeAddress}
      subtitle={language.confirmHomeAddressSubtitle}
      displayText={getAddress()}
      primaryActionButtonInfo={{
        text: language.confirmAddressBtn,
        onClick: handleConfirmAddressClicked,
      }}
      secondaryActionButtonInfo={{
        text: language.notNow,
        onClick: () => {
          ANALYTICS.logEvent(EVENT.CONFIRM_ADDRESS_NOT_NOW_CLICKED);
          dispatch(
            setCardDesignSelectionScreen(
              CARD_SELECTION_SCREEN.CONFIRM_ADDRESS_LATER
            )
          );
        },
      }}
    />
  );
}

function ConfirmAddressLater() {
  const { language, consumerInfo, apiScreenState, dispatch } = useImpState();

  const productUUID = useAppSelector(
    (state) => state.merchantDataSelector.productUUID
  );
  const multiCardUser = useAppSelector(
    (state) => state.loginSelector.multiCardUser
  );

  const isMultiCard = multiCardUser === true ? 'true' : 'false';
  const handleSoundsGood = () => {
    ANALYTICS.logEvent(EVENT.CONFIRM_ADDRESS_LATER_SOUNDS_GOOD_CLICKED);
    dispatch(setRedirectToLogin(true));
    redirectToAccountWithCookie(
      consumerInfo.auth.token,
      apiScreenState.deviceID,
      consumerInfo.consumerUUID,
      productUUID,
      isMultiCard
    );
  };

  useEffect(() => {
    ANALYTICS.logEvent(EVENT.CONFIRM_ADDRESS_LATER_VIEWED);
  }, []);

  return (
    <ImpContainer width="min(100% - 32px, 1200px)">
      <div className={styles.confirmLaterBody}>
        <h1 className={styles.confirmLaterTitle}>
          {language.youCanStillGet}
          <br />
          {language.aPhysicalCardLater}
        </h1>
        <p className={styles.confirmLaterText}>
          {language.requestInImprintApp}
        </p>
        <div className={styles.soundsGoodButtonContainer}>
          <ButtonServicing
            value={language.soundsGood}
            colorScheme={ButtonColorScheme.fillBranded}
            handleClick={handleSoundsGood}
          />
        </div>
      </div>
    </ImpContainer>
  );
}

interface MainCardDisplayProps {
  cardInfo: CardDesignOption;
  img: string;
}

function MainCardDisplay({ cardInfo, img }: MainCardDisplayProps) {
  const { language } = useImpState();

  if (!cardInfo) {
    return <></>;
  }

  return (
    <ImpContainer textAlign="center">
      <ImpFlex direction="column" align="center" fluid>
        <div className={styles.mainCard}>
          <img className={styles.mainCard} src={img} alt="main card"></img>
          {cardInfo?.status === CardDesignStatus.NOT_SELECTABLE && (
            <div className={styles.outOfStockOverlay}>
              <div className={styles.outOfStockBadge}>{cardInfo.reason}</div>
            </div>
          )}
        </div>
        <ImpSpacer height="m" />
        <ImpText typography="label1">{cardInfo.title}</ImpText>
        <ImpText typography="body2" color={colors.content.onBackgroundVariant}>
          {cardInfo?.fee ? convertAmountToString(cardInfo.fee) : language.free}
        </ImpText>
      </ImpFlex>
    </ImpContainer>
  );
}

function SelectCard() {
  const cardDesignOptions = useAppSelector(
    (state) => state.consumerInfoSelector.cardOffer.cardDesignOptions
  );
  const { language, merchantData, consumerInfo, dispatch } = useImpState();
  const cardDesignOptionsImages = merchantData.cardDesignOptionsImages;

  const cardDesignInfoMap: { [key: string]: CardDesignOption } =
    cardDesignOptions!.reduce(
      (acc, current) => {
        acc[current.cardDesignID] = current;
        return acc;
      },
      {} as { [key: string]: CardDesignOption }
    );

  const [currentSelection, setCurrentSelection] = useState('');

  useEffect(() => {
    ANALYTICS.logEvent(EVENT.CARD_ART_SELECTION_PAGE_VIEWED);

    const firstSelectable = cardDesignOptions?.find(
      (option) => option.status === CardDesignStatus.SELECTABLE
    );

    if (firstSelectable) {
      setCurrentSelection(firstSelectable.cardDesignID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleContinue() {
    ANALYTICS.logEvent(EVENT.CARD_ART_SELECTION_CHANGED, {
      card_design_id: currentSelection,
    });
    dispatch(
      confirm({
        token: consumerInfo.auth.token,
        applicationUUID: consumerInfo.auth.applicationUUID,
        cardDesignID: currentSelection,
        sendSms: false, // in store applications not supported for card design flow
      })
    );
    dispatch(setSelectedCardDesignID(currentSelection));
    dispatch(
      setCardDesignSelectionScreen(CARD_SELECTION_SCREEN.CONFIRM_ADDRESS)
    );
  }

  const primaryButtonState =
    cardDesignInfoMap[currentSelection]?.status === CardDesignStatus.SELECTABLE
      ? 'enabled'
      : 'disabled';

  return (
    <ImpContainer width="min(100% - 32px, 400px)">
      <ImpFlex direction="column" align="center">
        <div className={styles.header}>{language.chooseYourDesign}</div>
        <MainCardDisplay
          cardInfo={cardDesignInfoMap[currentSelection]}
          img={cardDesignOptionsImages[currentSelection]?.physical}
        />
        <ImpSpacer height="l" />
        <ImpDivider color={colors.border.borderVariant} />

        <div className={styles.cardSelection}>
          {cardDesignOptions!.map((cardDesignOption, index) => {
            const cardInfo = cardDesignOption;
            const imageSrc =
              cardDesignOptionsImages[cardInfo.cardDesignID]?.physical;

            if (
              cardInfo?.status === CardDesignStatus.SELECTABLE ||
              cardInfo?.status === CardDesignStatus.NOT_SELECTABLE
            ) {
              return (
                <ImpCardThumbnail
                  id={`card-thumbnail-${index}`}
                  key={index}
                  isSelected={
                    currentSelection === cardDesignOption.cardDesignID
                  }
                  src={imageSrc}
                  onClick={() => {
                    ANALYTICS.logEvent(EVENT.CARD_ART_SELECTION_CHANGED, {
                      card_status: cardDesignOption.status,
                      card_design_id: cardDesignOption.cardDesignID,
                    });
                    setCurrentSelection(cardDesignOption.cardDesignID);
                  }}
                />
              );
            } else {
              return <React.Fragment key={index}></React.Fragment>;
            }
          })}
        </div>

        <ImpSpacer height="xl" />
        <ImpContainer width="100%">
          <ImpButton
            size="fill"
            onClick={handleContinue}
            buttonStyle={'branded'}
            state={primaryButtonState}
          >
            {language.confirmCard}
          </ImpButton>
        </ImpContainer>
      </ImpFlex>
    </ImpContainer>
  );
}

function CardDesignScreen({ screen }: { screen: CARD_SELECTION_SCREEN }) {
  switch (screen) {
    case CARD_SELECTION_SCREEN.LOADING:
      return <Spinner />;
    case CARD_SELECTION_SCREEN.CONFIRM_ADDRESS:
      return <CardDesignConfirmation />;
    case CARD_SELECTION_SCREEN.CARD_SELECTION:
      return <SelectCard />;
    case CARD_SELECTION_SCREEN.CONFIRM_ADDRESS_LATER:
      return <ConfirmAddressLater />;
    default:
      return null;
  }
}

export default function CardDesignSelectionFlow() {
  const cardDesignSelector: any = useAppSelector(
    (state) => state.cardDesignSelector
  );
  const dispatch = useDispatch();

  const { merchantData, appStatus } = useImpState();
  const cardDesignOptions = useAppSelector(
    (state) => state.consumerInfoSelector.cardOffer.cardDesignOptions
  );
  const cardDesignOptionsImages = merchantData.cardDesignOptionsImages;

  useEffect(() => {
    if (appStatus.productAccountUUID) {
      dispatch(
        setCardDesignSelectionScreen(CARD_SELECTION_SCREEN.CONFIRM_ADDRESS)
      );
    } else if (cardDesignOptionsImages[cardDesignOptions![0]?.cardDesignID]) {
      dispatch(
        setCardDesignSelectionScreen(CARD_SELECTION_SCREEN.CARD_SELECTION)
      );
    } else {
      dispatch(setCardDesignSelectionScreen(CARD_SELECTION_SCREEN.LOADING));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardDesignOptionsImages, cardDesignOptions]);

  return (
    <ImpContainer width="100%">
      <ImpSpacer height="2xl" mobileHeight="xl" />
      <ImpFlex direction="row" justify="center" fluid>
        <CardDesignScreen screen={cardDesignSelector.screen} />
      </ImpFlex>
    </ImpContainer>
  );
}
